// src/pages/platform-admin/PlatformTopicView.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
    Avatar,
    Tabs,
    Tab,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel,
    Checkbox,
    Badge,
    Stack,
    LinearProgress,
    Divider
} from '@mui/material';
import {
    Search,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    CheckCircle,
    Cancel,
    Schedule,
    Assignment,
    Science,
    MenuBook,
    Lightbulb,
    School,
    SupervisorAccount,
    DateRange,
    ExpandMore,
    Assessment,
    Person,
    Email,
    Phone,
    Flag,
    TrendingUp,
    Article,
    Description,
    LocalOffer,
    QuestionAnswer,
    Feedback,
    Verified,
    Pending,
    Send,
    TaskAlt,
    Update,
    Warning as WarningIcon,
    Info,
    Analytics,
    Category,
    LocationCity
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchTopicList,
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import authService from '../../apiServices/authService';
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import SupervisorBar from '../../components/SupervisorBar';
import StudentBar from '../../components/StudentBar';
import InstitutionHeadBar from '../../components/InstitutionHeadBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to parse JSON fields
const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    
    try {
        let parsed = JSON.parse(field);
        while (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                break;
            }
        }
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        try {
            let cleaned = field;
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                cleaned = cleaned.slice(1, -1);
            }
            cleaned = cleaned.replace(/\\"/g, '"');
            cleaned = cleaned.replace(/\\\\/g, '\\');
            const parsed = JSON.parse(cleaned);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e2) {
            console.warn('Failed to parse JSON field:', field, e2);
            return [];
        }
    }
};

// Topic status options
const topicStatusOptions = [
    { id: 'draft', name: 'Draft', color: 'default', icon: <Description />, description: 'Initial draft, not submitted yet' },
    { id: 'submitted', name: 'Submitted', color: 'info', icon: <Send />, description: 'Submitted for review' },
    { id: 'under_review', name: 'Under Review', color: 'warning', icon: <Schedule />, description: 'Currently under review by supervisor' },
    { id: 'revision_required', name: 'Revision Required', color: 'warning', icon: <Feedback />, description: 'Changes requested by supervisor' },
    { id: 'approved', name: 'Approved', color: 'success', icon: <CheckCircle />, description: 'Topic approved by supervisor' },
    { id: 'rejected', name: 'Rejected', color: 'error', icon: <Cancel />, description: 'Topic rejected by supervisor' },
    { id: 'completed', name: 'Completed', color: 'success', icon: <TaskAlt />, description: 'Research completed' },
    { id: 'superseded', name: 'Superseded', color: 'secondary', icon: <Update />, description: 'Replaced by newer version' }
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PlatformTopicView = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [topics, setTopics] = useState([]);
    const [filteredTopics, setFilteredTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [filterOptions, setFilterOptions] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        draft: 0,
        submitted: 0,
        under_review: 0,
        revision_required: 0,
        approved: 0,
        rejected: 0,
        completed: 0
    });
    
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");
    const userType = authService.getUserType();

    // Filters
    const [filters, setFilters] = useState({
        topic_status: '',
        student_name: '',
        supervisor_name: '',
        specialty_id: '',
        university_id: '',
        dateFrom: '',
        dateTo: ''
    });

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        
        // Auto-refresh every 3 minutes
        const interval = setInterval(() => {
            fetchData(true);
        }, 180000);
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        applyFiltersAndSort();
    }, [topics, searchTerm, filters, activeTab]);

    const fetchData = async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);

        try {
            // Fetch all research topics for admin
            const result = await apiService.list(APIResearchTopicList);

            if (result.success && isMounted.current) {
                let topicsData = Array.isArray(result.data) ? result.data : [];
                
                // Parse JSON fields for each topic
                topicsData = topicsData.map(topic => ({
                    ...topic,
                    keywords: parseJsonField(topic.keywords),
                    research_questions: parseJsonField(topic.research_questions),
                    objectives: parseJsonField(topic.objectives)
                })).sort((a, b) => new Date(b.submitted_date) - new Date(a.submitted_date));

                setTopics(topicsData);
                calculateStats(topicsData);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current && !silent) {
                setLoading(false);
            }
        }
    };

    const calculateStats = (topicsData) => {
        const newStats = {
            total: topicsData.length,
            draft: topicsData.filter(t => t.topic_status === 'draft').length,
            submitted: topicsData.filter(t => t.topic_status === 'submitted').length,
            under_review: topicsData.filter(t => t.topic_status === 'under_review').length,
            revision_required: topicsData.filter(t => t.topic_status === 'revision_required').length,
            approved: topicsData.filter(t => t.topic_status === 'approved').length,
            rejected: topicsData.filter(t => t.topic_status === 'rejected').length,
            completed: topicsData.filter(t => t.topic_status === 'completed').length
        };
        setStats(newStats);
    };

    // Get unique specialties from topics for filter dropdown
    const getUniqueSpecialties = () => {
        const specialties = new Map();
        topics.forEach(topic => {
            if (topic.specialty_id && topic.specialty_name) {
                specialties.set(topic.specialty_id, {
                    id: topic.specialty_id,
                    name: topic.specialty_name,
                    code: topic.specialty_code
                });
            }
        });
        return Array.from(specialties.values());
    };

    // Get unique universities from topics for filter dropdown
    const getUniqueUniversities = () => {
        const universities = new Map();
        topics.forEach(topic => {
            if (topic.student_university_id && topic.student_university_name) {
                universities.set(topic.student_university_id, {
                    id: topic.student_university_id,
                    name: topic.student_university_name
                });
            }
        });
        return Array.from(universities.values());
    };

    const applyFiltersAndSort = () => {
        let filtered = [...topics];
        
        // Filter by tab
        if (activeTab === 1) filtered = filtered.filter(t => t.topic_status === 'submitted');
        else if (activeTab === 2) filtered = filtered.filter(t => t.topic_status === 'under_review');
        else if (activeTab === 3) filtered = filtered.filter(t => t.topic_status === 'revision_required');
        else if (activeTab === 4) filtered = filtered.filter(t => t.topic_status === 'approved');
        else if (activeTab === 5) filtered = filtered.filter(t => t.topic_status === 'rejected');
        else if (activeTab === 6) filtered = filtered.filter(t => t.topic_status === 'completed');
        else if (activeTab === 7) filtered = filtered.filter(t => t.topic_status === 'draft');
        
        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t => 
                t.topic_title?.toLowerCase().includes(term) ||
                t.topic_id?.toLowerCase().includes(term) ||
                t.student_name?.toLowerCase().includes(term) ||
                t.student_registration_id?.toLowerCase().includes(term) ||
                t.supervisor_name?.toLowerCase().includes(term) ||
                t.specialty_name?.toLowerCase().includes(term) ||
                t.student_university_name?.toLowerCase().includes(term)
            );
        }
        
        // Filter by status dropdown
        if (filters.topic_status) {
            filtered = filtered.filter(t => t.topic_status === filters.topic_status);
        }
        
        // Filter by student name
        if (filters.student_name) {
            filtered = filtered.filter(t => 
                t.student_name?.toLowerCase().includes(filters.student_name.toLowerCase())
            );
        }
        
        // Filter by supervisor name
        if (filters.supervisor_name) {
            filtered = filtered.filter(t => 
                t.supervisor_name?.toLowerCase().includes(filters.supervisor_name.toLowerCase())
            );
        }
        
        // Filter by specialty
        if (filters.specialty_id) {
            filtered = filtered.filter(t => t.specialty_id === parseInt(filters.specialty_id));
        }

        // Filter by university
        if (filters.university_id) {
            filtered = filtered.filter(t => t.student_university_id === parseInt(filters.university_id));
        }
        
        // Filter by date range
        if (filters.dateFrom) {
            filtered = filtered.filter(t => new Date(t.submitted_date) >= new Date(filters.dateFrom));
        }
        if (filters.dateTo) {
            filtered = filtered.filter(t => new Date(t.submitted_date) <= new Date(filters.dateTo));
        }
        
        setFilteredTopics(filtered);
        setPage(0);
    };

    const handleOpenViewDialog = (topic) => {
        setSelectedTopic(topic);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedTopic(null);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            topic_status: '',
            student_name: '',
            supervisor_name: '',
            specialty_id: '',
            university_id: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setActiveTab(0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusIcon = (status) => {
        const option = topicStatusOptions.find(s => s.id === status);
        return option?.icon || <Schedule />;
    };

    const getStatusColor = (status) => {
        const option = topicStatusOptions.find(s => s.id === status);
        return option?.color || 'default';
    };

    const getStatusInfo = (status) => {
        const option = topicStatusOptions.find(s => s.id === status);
        return option?.description || '';
    };

    if (loading) {
        return <LoadingState message="Loading Research Topics..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Research Topics" variant="research" fullPage retryText="Retry Loading" />;
    }
    const determinTopBar = () => {
        if (userType === "institution_admin") {
            return <InstitutionAdminBar />;
        } else if (userType === "supervisor") {
            return <SupervisorBar />;
        } else if (userType === "institution_head") {
            return <InstitutionHeadBar />;
        } else if (userType === "student") {
            return <StudentBar />;
        } else {
            return <PlatformAdminBar notificationCount={65} />;
        }
    }

    return (
        <>
                <FullWidthContainer>
                {determinTopBar()}
                
                
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                            <Assessment /> Research Topics Management
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={() => setFilterOptions(true)}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Filter
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={() => fetchData()}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search by topic title, ID, student name, registration ID, supervisor name, specialty, or university..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Stats Cards */}
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.primary.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.total}</Typography>
                                    <Typography variant="caption">Total Topics</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.grey.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.draft}</Typography>
                                    <Typography variant="caption">Draft</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.info.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.submitted}</Typography>
                                    <Typography variant="caption">Submitted</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.under_review}</Typography>
                                    <Typography variant="caption">Under Review</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light + '20' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.revision_required}</Typography>
                                    <Typography variant="caption">Revision</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.approved}</Typography>
                                    <Typography variant="caption">Approved</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.error.light + '10' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.rejected}</Typography>
                                    <Typography variant="caption">Rejected</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3} md={1.5}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light + '20' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{stats.completed}</Typography>
                                    <Typography variant="caption">Completed</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 100 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="All" />
                        <Tab label="Draft" />
                        <Tab label="Submitted" />
                        <Tab label="Under Review" />
                        <Tab label="Revision Required" />
                        <Tab label="Approved" />
                        <Tab label="Rejected" />
                        <Tab label="Completed" />
                    </Tabs>
                </Paper>

                {/* Topics Table */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'auto', backgroundColor: 'white' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 700, sm: 900, md: 1100 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': { color: 'white', fontWeight: 'bold' }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Student</TableCell>
                                        <TableCell>Supervisor</TableCell>
                                        <TableCell>Topic Title</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Submitted Date</TableCell>
                                        <TableCell>Assigned</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredTopics.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((topic, index) => (
                                        <TableRow key={topic.id} hover>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.info.main }}>
                                                        {topic.student_name?.charAt(0) || 'S'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {topic.student_name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {topic.supervisor_name ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.secondary.main }}>
                                                            {topic.supervisor_name?.charAt(0) || 'S'}
                                                        </Avatar>
                                                        <Typography variant="body2">
                                                            {topic.supervisor_name}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Not Assigned" size="small" variant="outlined" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {topic.topic_title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={getStatusInfo(topic.topic_status)}>
                                                    <Chip
                                                        icon={getStatusIcon(topic.topic_status)}
                                                        label={topicStatusOptions.find(s => s.id === topic.topic_status)?.name || topic.topic_status}
                                                        color={getStatusColor(topic.topic_status)}
                                                        size="small"
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(topic.submitted_date)}</TableCell>
                                            <TableCell>{topic.assigned_date}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => handleOpenViewDialog(topic)} color="primary" size="small">
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredTopics.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                        {filteredTopics.length === 0 && (
                            <Box sx={{ py: 8, textAlign: 'center' }}>
                                <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">No research topics found</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {searchTerm || filters.topic_status || filters.student_name || filters.supervisor_name || filters.university_id
                                        ? 'Try adjusting your filters' 
                                        : 'No topics have been created yet'}
                                </Typography>
                                {(searchTerm || filters.topic_status || filters.student_name || filters.supervisor_name || filters.university_id) && (
                                    <Button onClick={clearFilters} sx={{ mt: 2 }}>
                                        Clear Filters
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* View Details Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
                >
                    <DialogTitle sx={{ 
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Research Topic Details</Typography>
                            <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedTopic && (
                            <Box sx={{ pt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                    <Chip label={selectedTopic.topic_id} color="primary" />
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {selectedTopic.specialty_name && (
                                            <Chip
                                                icon={<Category />}
                                                label={selectedTopic.specialty_name}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {selectedTopic.student_university_name && (
                                            <Chip
                                                icon={<LocationCity />}
                                                label={selectedTopic.student_university_name}
                                                color="info"
                                                variant="outlined"
                                            />
                                        )}
                                        <Tooltip title={getStatusInfo(selectedTopic.topic_status)}>
                                            <Chip
                                                icon={getStatusIcon(selectedTopic.topic_status)}
                                                label={topicStatusOptions.find(s => s.id === selectedTopic.topic_status)?.name}
                                                color={getStatusColor(selectedTopic.topic_status)}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Student Information */}
                                <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.info.light + '10' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Person /> Student Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2"><strong>Name:</strong> {selectedTopic.student_name || 'N/A'}</Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}><strong>Registration ID:</strong> {selectedTopic.student_registration_id || 'N/A'}</Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}><strong>University:</strong> {selectedTopic.student_university_name || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2"><strong>Student ID:</strong> {selectedTopic.student_id || 'N/A'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Supervisor Information */}
                                <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.secondary.light + '10' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SupervisorAccount /> Supervisor Information
                                    </Typography>
                                    {selectedTopic.supervisor_name ? (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2"><strong>Name:</strong> {selectedTopic.supervisor_name}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2"><strong>Supervisor ID:</strong> {selectedTopic.supervisor_id || 'N/A'}</Typography>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No supervisor assigned yet</Typography>
                                    )}
                                </Paper>

                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Lightbulb color="primary" /> Topic Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Title:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.topic_title}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Description:</strong></Typography>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTopic.topic_description}</Typography>
                                            </Grid>
                                            {selectedTopic.specialty_name && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2"><strong>Research Specialty:</strong></Typography>
                                                    <Chip 
                                                        icon={<Category />}
                                                        label={selectedTopic.specialty_name}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                    {selectedTopic.specialty_code && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            (Code: {selectedTopic.specialty_code})
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            )}
                                            {selectedTopic.student_university_name && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2"><strong>Student University:</strong></Typography>
                                                    <Chip 
                                                        icon={<LocationCity />}
                                                        label={selectedTopic.student_university_name}
                                                        size="small"
                                                        color="info"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Science color="secondary" /> Research Details
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Keywords:</strong></Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                    {Array.isArray(selectedTopic.keywords) && selectedTopic.keywords.length > 0 ? (
                                                        selectedTopic.keywords.map((k, i) => (
                                                            <Chip key={i} label={k} size="small" />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No keywords provided</Typography>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Research Questions:</strong></Typography>
                                                {Array.isArray(selectedTopic.research_questions) && selectedTopic.research_questions.length > 0 ? (
                                                    <ul>
                                                        {selectedTopic.research_questions.map((q, i) => (
                                                            <li key={i}><Typography variant="body2">{q}</Typography></li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No research questions provided</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Objectives:</strong></Typography>
                                                {Array.isArray(selectedTopic.objectives) && selectedTopic.objectives.length > 0 ? (
                                                    <ul>
                                                        {selectedTopic.objectives.map((o, i) => (
                                                            <li key={i}><Typography variant="body2">{o}</Typography></li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No objectives provided</Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MenuBook color="info" /> Methodology & Contribution
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Methodology:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.methodology_brief || 'Not provided'}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Literature Review:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.literature_review_summary || 'Not provided'}</Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><strong>Expected Contribution:</strong></Typography>
                                                <Typography variant="body2">{selectedTopic.expected_contribution || 'Not provided'}</Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Dates Information */}
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DateRange color="action" /> Timeline Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2"><strong>Submitted Date:</strong> {formatCreatedAtDate(selectedTopic.submitted_date)}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2"><strong>Last Modified:</strong> {formatCreatedAtDate(selectedTopic.last_modified_date)}</Typography>
                                            </Grid>
                                            {selectedTopic.approval_date && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Approval Date:</strong> {formatCreatedAtDate(selectedTopic.approval_date)}</Typography>
                                                </Grid>
                                            )}
                                            {selectedTopic.review_date && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Review Date:</strong> {formatCreatedAtDate(selectedTopic.review_date)}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Feedback Section */}
                                {selectedTopic.supervisor_feedback && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.warning.main }}>
                                                <Feedback /> Supervisor Feedback
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTopic.supervisor_feedback}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Review Status Information */}
                                <Paper sx={{ p: 2, mt: 2, bgcolor: theme.palette.grey[50] }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Analytics /> Review & Approval Status
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2">
                                                <strong>Topic Status:</strong>{' '}
                                                <Chip
                                                    size="small"
                                                    icon={getStatusIcon(selectedTopic.topic_status)}
                                                    label={topicStatusOptions.find(s => s.id === selectedTopic.topic_status)?.name}
                                                    color={getStatusColor(selectedTopic.topic_status)}
                                                />
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2">
                                                <strong>Review Status:</strong>{' '}
                                                <Chip
                                                    size="small"
                                                    label={selectedTopic.formatted_review_status || selectedTopic.review_status}
                                                    color={selectedTopic.review_status === 'review_completed' ? 'success' : 'warning'}
                                                />
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2">
                                                <strong>Approval Status:</strong>{' '}
                                                <Chip
                                                    size="small"
                                                    label={selectedTopic.formatted_approval_status || selectedTopic.approval_status}
                                                    color={selectedTopic.approval_status === 'approved' ? 'success' : selectedTopic.approval_status === 'rejected_with_feedback' ? 'error' : 'warning'}
                                                />
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseViewDialog} variant="contained">Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Filter Options Dialog */}
                <Dialog open={filterOptions} onClose={() => setFilterOptions(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Filter Options</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Topic Status</InputLabel>
                                <Select
                                    name="topic_status"
                                    value={filters.topic_status}
                                    onChange={handleFilterChange}
                                    label="Topic Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    {topicStatusOptions.map((status) => (
                                        <MenuItem key={status.id} value={status.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {status.icon} {status.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <TextField
                                fullWidth
                                label="Student Name"
                                name="student_name"
                                value={filters.student_name}
                                onChange={handleFilterChange}
                                size="small"
                                placeholder="Filter by student name"
                            />
                            
                            <TextField
                                fullWidth
                                label="Supervisor Name"
                                name="supervisor_name"
                                value={filters.supervisor_name}
                                onChange={handleFilterChange}
                                size="small"
                                placeholder="Filter by supervisor name"
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Research Specialty</InputLabel>
                                <Select
                                    name="specialty_id"
                                    value={filters.specialty_id}
                                    onChange={handleFilterChange}
                                    label="Research Specialty"
                                >
                                    <MenuItem value="">All Specialties</MenuItem>
                                    {getUniqueSpecialties().map((specialty) => (
                                        <MenuItem key={specialty.id} value={specialty.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Category />
                                                {specialty.name}
                                                {specialty.code && ` (${specialty.code})`}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Student University</InputLabel>
                                <Select
                                    name="university_id"
                                    value={filters.university_id}
                                    onChange={handleFilterChange}
                                    label="Student University"
                                >
                                    <MenuItem value="">All Universities</MenuItem>
                                    {getUniqueUniversities().map((university) => (
                                        <MenuItem key={university.id} value={university.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocationCity />
                                                {university.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <TextField
                                fullWidth
                                label="From Date"
                                type="date"
                                name="dateFrom"
                                value={filters.dateFrom}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                            
                            <TextField
                                fullWidth
                                label="To Date"
                                type="date"
                                name="dateTo"
                                value={filters.dateTo}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={clearFilters}>Clear All</Button>
                        <Button onClick={() => setFilterOptions(false)} variant="contained">Apply Filters</Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default PlatformTopicView;